<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { AutoForm } from "@/ui-plus/auto-form";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { z } from "zod";
import { LOCAL_STORAGE_KEYS } from "@auth-frontend-vue/const";
import { checkPhone } from "@auth-frontend-vue/lib/auth.actions";
import { useFormPlus } from "@/ui-plus/form/useFormPlus";
import { Loader2 } from "lucide-vue-next";
import AutoFormFieldPhone from "@/ui-plus/PhoneNumber/AutoFormFieldPhone.vue";
import { phoneNumberSchema } from "@lib/zod/PhoneNumberArray";
import { nextTick, onMounted, onUnmounted, useTemplateRef, watch } from "vue";

const props = defineProps<{
    /** Prefill from email step when input looked like a phone number */
    initialPhoneNumber?: string;
}>();

const emit = defineEmits<{
    checked: [result: {
        phoneNumber: string;
        exists: boolean;
        hasPassword: boolean;
        password: string;
    }],
    /** Optional seed to continue typing in the email field */
    useEmailInstead: [seed?: string],
}>();

/** password managers autofill on this step; hand off to PhoneLoginForm */
const autofillPasswordInput = useTemplateRef<HTMLInputElement>('autofillPasswordInput');

const schema = z.object({
    phoneNumber: phoneNumberSchema,
});

const form = useForm({
    validationSchema: toTypedSchema(schema)
});

const { isLoading, globalError, handleError, withSubmit } = useFormPlus(
    form,
    props.initialPhoneNumber
        ? undefined
        : {
            persistenceFields: {
                phoneNumber: LOCAL_STORAGE_KEYS.REMEMBER_LAST_PHONE
            }
        }
);

/** Letters or @ → treat as email, not phone */
function isUnlikelyPhone(value: string): boolean {
    const v = value.trim();
    if (!v) return false;
    return v.includes('@') || /[a-zA-Z]/.test(v);
}

let phoneInputEl: HTMLInputElement | null = null;

function switchToEmailWith(seed: string) {
    emit('useEmailInstead', seed);
}

function onPhoneBeforeInput(e: Event) {
    const ev = e as InputEvent;
    const data = ev.data;
    if (!data || !/[a-zA-Z@]/.test(data)) return;

    ev.preventDefault();
    const inputEl = ev.target as HTMLInputElement;
    const start = inputEl.selectionStart ?? inputEl.value.length;
    const end = inputEl.selectionEnd ?? inputEl.value.length;
    const next = inputEl.value.slice(0, start) + data + inputEl.value.slice(end);
    switchToEmailWith(next);
}

function bindPhoneInputListeners() {
    phoneInputEl = document.querySelector<HTMLInputElement>('input[name="phoneNumber"]');
    phoneInputEl?.addEventListener('beforeinput', onPhoneBeforeInput);
}

function unbindPhoneInputListeners() {
    phoneInputEl?.removeEventListener('beforeinput', onPhoneBeforeInput);
    phoneInputEl = null;
}

// Paste / programmatic values that already contain letters/@
watch(
    () => form.values.phoneNumber,
    (value) => {
        if (typeof value === 'string' && isUnlikelyPhone(value)) {
            switchToEmailWith(value.trim());
        }
    }
);

onMounted(async () => {
    await nextTick();
    if (props.initialPhoneNumber) {
        form.setFieldValue('phoneNumber', props.initialPhoneNumber);
    }
    // Keep typing after email→phone switch (phone input mounts after country fetch)
    setTimeout(() => {
        bindPhoneInputListeners();
        const input = document.querySelector<HTMLInputElement>('input[name="phoneNumber"]');
        if (!input) return;
        input.focus();
        const len = input.value.length;
        input.setSelectionRange(len, len);
    }, 300);
});

onUnmounted(() => {
    unbindPhoneInputListeners();
});

async function onSubmit(values: z.infer<typeof schema>) {
    await withSubmit(async () => {
        const { data, error } = await checkPhone({
            phoneNumber: values.phoneNumber,
        });
        if (error) {
            handleError(error, form);
        } else {
            emit('checked', {
                phoneNumber: values.phoneNumber,
                exists: data.exists,
                hasPassword: data.exists ? data.hasPassword : false,
                password: autofillPasswordInput.value?.value ?? '',
            });
        }
    })
}
</script>

<template>
    <div>
        <h1 class="text-2xl font-bold">{{ $t('signInOrCreate') }}</h1>
        <p class="text-sm text-muted-foreground mt-1">{{ $t('enterPhoneToContinue') }}</p>

        <AutoForm class="space-y-6 mt-6"
                  :schema="schema"
                  :form="form"
                  @submit="onSubmit"
                  :field-config="{
                    phoneNumber: {
                        // label: $t('phoneNumber'),
                        hideLabel: true,
                        component: AutoFormFieldPhone,
                        inputProps: {
                            autofocus: true,
                        },
                    },
                }">
            <!-- password managers: username (phone) + password pair (same pattern as EmailStepForm) -->
            <input type="tel"
                   :value="form.values.phoneNumber || initialPhoneNumber || ''"
                   name="username"
                   autocomplete="username"
                   readonly
                   tabindex="-1"
                   aria-hidden="true"
                   class="sr-only" />
            <input ref="autofillPasswordInput"
                   type="password"
                   name="password"
                   autocomplete="current-password"
                   tabindex="-1"
                   aria-hidden="true"
                   class="sr-only" />
            <div class="text-sm font-medium text-destructive"
                 v-if="globalError">
                {{ globalError }}
            </div>
            <div>
                <Button type="submit"
                        :disabled="isLoading"
                        class="w-full my-6">
                    <Loader2 v-if="isLoading"
                             class="animate-spin" />
                    {{ $t('continue') }}
                </Button>
            </div>
        </AutoForm>
        <div class="text-center">
            <button type="button"
                    class="text-sm underline"
                    @click="emit('useEmailInstead')">
                {{ $t('continueWithEmail') }}
            </button>
        </div>
    </div>
</template>
